import {
  BaseControl,
  SelectControl,
  ToggleControl,
} from "@wordpress/components";
import { applyFilters } from "@wordpress/hooks";
import { __ } from "@wordpress/i18n";

import ColorPicker from "../components/ColorPicker";
import {
  DOTS_COLOR_LABELS,
  DOTS_COLOR_VARS,
  NAV_COLOR_LABELS,
  NAV_COLOR_VARS,
} from "./slider-control-colors";

/**
 * Per-instance slider controls picker (placement / nav / dots).
 *
 * Three orthogonal axes: `placement` is WHERE the control cluster sits, `nav` is
 * the arrow skin, `dots` is the pagination style. Any combination is valid — a
 * grouped row with default arrows and fraction dots is as legal as the naked
 * chevrons and progress pill. Folding placement into the nav key would need a
 * grouped-blank / grouped-default / grouped-below-blank … explosion.
 *
 * The empty value ("") means "inherit the global default" (theme.json custom
 * setting / the brandy_slider_controls filter). Only a non-empty selection
 * overrides on this instance — see SliderControls::styles() precedence.
 *
 * Themes register frontend variants via window.brandyEmbla.{nav,dots}.register()
 * and add them to these dropdowns with the JS filters below, e.g.:
 *   addFilter('brandy.sliderControls.dotsOptions', 'my-theme', (opts) =>
 *     [...opts, { label: 'Thumbnails', value: 'thumbnails' }]);
 */

const INHERIT = { label: __("Inherit (global)", "brandy-blocks"), value: "" };

/**
 * The four arrow colour pickers, on their own.
 *
 * Separate from the panel below because Showcase Slider wants these without the
 * variant dropdowns: it pins its own nav and dots styles (its counter and its
 * edge-preview strip are built around them), so offering those axes there would
 * present a choice the block ignores.
 *
 * Unset inherits, exactly as "" does on the variant axes — theme.json
 * settings.custom.brandy.slider.navColors and the brandy_slider_nav_colors
 * filter both resolve server-side, so an untouched picker previews as the
 * built-in default rather than the site's brand value.
 *
 * @param {Object}   props
 * @param {Object}   props.attributes    Object holding `controlsNavColors`.
 * @param {Function} props.setAttributes Setter for that same object.
 * @return {Element} The pickers.
 */
/**
 * A group of colour pickers bound to one object attribute.
 *
 * @param {Object}   props
 * @param {Object}   props.attributes    Object holding `attrKey`.
 * @param {Function} props.setAttributes Setter for that same object.
 * @param {string}   props.attrKey       Attribute name to read and write.
 * @param {Object}   props.map           Canonical key => custom property.
 * @param {Object}   props.labels        Canonical key => picker label.
 * @return {Element} The pickers.
 */
function ColorPickerGroup({ attributes, setAttributes, attrKey, map, labels }) {
  const colors = attributes[attrKey] ?? {};

  /*
   * Clearing a picker deletes the key rather than storing '', so "no override"
   * stays one state. Kept as an object attribute (not one flat key per colour)
   * because the loop blocks nest the whole `controls*` family inside their own
   * `slider` attribute, and one key nests without widening that object again
   * for every colour the axis grows.
   */
  const setColor = (key, value) => {
    const next = { ...colors };

    if (value) {
      next[key] = value;
    } else {
      delete next[key];
    }

    setAttributes({ [attrKey]: next });
  };

  /* The picker stores a palette choice as `var:preset|color|<slug>`, so a
     slider recoloured from the palette follows a retheme instead of pinning the
     hex the swatch happened to hold. */
  return Object.keys(map).map((key) => (
    <BaseControl key={key} __nextHasNoMarginBottom>
      <BaseControl.VisualLabel>{labels[key]}</BaseControl.VisualLabel>
      <ColorPicker
        color={colors[key]}
        onChange={(value) => setColor(key, value)}
      />
    </BaseControl>
  ));
}

/**
 * The arrow colour pickers, on their own.
 *
 * Separate from the panel below because Showcase Slider wants the colours
 * without the variant dropdowns: it pins its own nav and dots styles (its
 * counter and its edge-preview strip are built around them), so offering those
 * axes there would present a choice the block ignores.
 *
 * Unset inherits, exactly as "" does on the variant axes — theme.json
 * settings.custom.brandy.slider.navColors and the brandy_slider_nav_colors
 * filter both resolve server-side, so an untouched picker previews as the
 * built-in default rather than the site's brand value.
 *
 * @param {Object} props Attributes/setter pair.
 * @return {Element} The pickers.
 */
export function SliderNavColorsControls(props) {
  return (
    <ColorPickerGroup
      {...props}
      attrKey="controlsNavColors"
      map={NAV_COLOR_VARS}
      labels={NAV_COLOR_LABELS}
    />
  );
}

/**
 * The pagination colour pickers, on their own.
 *
 * @param {Object} props Attributes/setter pair.
 * @return {Element} The pickers.
 */
export function SliderDotsColorsControls(props) {
  return (
    <ColorPickerGroup
      {...props}
      attrKey="controlsDotsColors"
      map={DOTS_COLOR_VARS}
      labels={DOTS_COLOR_LABELS}
    />
  );
}

export default function SliderControlsPanel({
  attributes,
  setAttributes,
  // Defaults to true so every existing caller renders exactly as before. Only
  // a single-slide block passes false: dimming "outer" slides needs a row of
  // them to be outer to.
  showDimControl = true,
}) {

  const placementOptions = applyFilters(
    "brandy.sliderControls.placementOptions",
    [
      INHERIT,
      { label: __("Arrows at edges", "brandy-blocks"), value: "default" },
      {
        label: __("Grouped — over slider", "brandy-blocks"),
        value: "grouped-bottom",
      },
      {
        label: __("Grouped — below slider", "brandy-blocks"),
        value: "grouped-below",
      },
    ]
  );

  const navOptions = applyFilters("brandy.sliderControls.navOptions", [
    INHERIT,
    { label: __("Default arrows", "brandy-blocks"), value: "default" },
  ]);

  const dotsOptions = applyFilters("brandy.sliderControls.dotsOptions", [
    INHERIT,
    { label: __("Dots", "brandy-blocks"), value: "default" },
    { label: __("Fraction (01 / 06)", "brandy-blocks"), value: "fraction" },
  ]);

  const revealOptions = applyFilters("brandy.sliderControls.revealOptions", [
    INHERIT,
    { label: __("Always visible", "brandy-blocks"), value: "always" },
    { label: __("On hover", "brandy-blocks"), value: "hover" },
  ]);

  return (
    <>
      <SelectControl
        __nextHasNoMarginBottom
        label={__("Controls placement", "brandy-blocks")}
        help={__(
          "Where the arrows and dots sit. The canvas preview shows the default when this is set to inherit — the global value is resolved server-side.",
          "brandy-blocks"
        )}
        value={attributes.controlsPlacement ?? ""}
        options={placementOptions}
        onChange={(v) => setAttributes({ controlsPlacement: v })}
      />
      <SelectControl
        __nextHasNoMarginBottom
        label={__("Navigation style", "brandy-blocks")}
        value={attributes.controlsNav ?? ""}
        options={navOptions}
        onChange={(v) => setAttributes({ controlsNav: v })}
      />
      <SelectControl
        __nextHasNoMarginBottom
        label={__("Dots style", "brandy-blocks")}
        value={attributes.controlsDots ?? ""}
        options={dotsOptions}
        onChange={(v) => setAttributes({ controlsDots: v })}
      />
      <SelectControl
        __nextHasNoMarginBottom
        label={__("Show controls", "brandy-blocks")}
        help={__(
          "On hover, each control returns from its own edge. Touch devices always show the controls — there is no hover there to reveal them with.",
          "brandy-blocks"
        )}
        value={attributes.controlsReveal ?? ""}
        options={revealOptions}
        onChange={(v) => setAttributes({ controlsReveal: v })}
      />
      {/*
        Writes into whichever object this panel was handed: the block's own
        attributes for Slider/Testimonials/MediaGallery, or the nested `slider`
        object for the loop blocks, which pass it through updateSlider. Both
        reach SliderControls::data_attrs() by the same route the control axes
        above already take.
      */}
      {showDimControl && (
        <ToggleControl
          __nextHasNoMarginBottom
          label={__("Dim outer slides", "brandy-blocks")}
          help={__(
            "Fades the slide at each edge of the row and keeps the middle lit, so a slide stays dimmed until it reaches the centre. Desktop only, and the canvas does not preview it — the effect needs the live slider.",
            "brandy-blocks"
          )}
          checked={!!attributes.dimInactiveSlides}
          onChange={(v) => setAttributes({ dimInactiveSlides: v })}
        />
      )}
      <SliderNavColorsControls
        attributes={attributes}
        setAttributes={setAttributes}
      />
      <SliderDotsColorsControls
        attributes={attributes}
        setAttributes={setAttributes}
      />
    </>
  );
}
